import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CommonService } from '@app/services/common.service';
import {
  FilePlaceholder,
  FilePlaceholderResponse,
} from '@features/messages/interfaces/whatsapp-messages.interface';

@Injectable({
  providedIn: 'root',
})
export class ProcessService {
  private apiUrl = `${environment.baseAPIUrl}/process-schedulers`;

  constructor(private http: HttpClient, private commonService: CommonService) {}

  getProcessAssignableUsers(
    roles: string,
    limit: number = 20,
    skip: number = 0,
    term: string = ''
  ): Observable<any> {
    let params = new HttpParams()
      .set('type', roles)
      .set('limit', limit.toString())
      .set('skip', skip.toString())
      .set('withMembers', 'true')
      .set('sortBy', 'createdAt')
      .set('sortOrder', 'desc')
      .set('showAdmin', 'true');

    if (term && term.trim() !== '') {
      params = params.set('filter', term);
    }

    return this.http.get<any>(`${environment.baseAPIUrl}/users`, {
      headers: this.commonService.getRequestHeaders(),
      params,
    });
  }

  getProcessSchedulers(
    skip: number,
    limit: number,
    searchValue: string = '',
    sortColumn: string = 'startDateTime',
    sortDirection: string = 'asc',
    createdFromDate?: string,
    createdToDate?: string,
    addedBy?: string,
    clinic?: string,
    category?: string
  ): Observable<any> {
    let params = new HttpParams()
      .set('skip', skip.toString())
      .set('limit', limit.toString())
      .set('sortBy', sortColumn)
      .set('sortOrder', sortDirection);

    if (searchValue) {
      params = params.set('filter', searchValue);
    }
    if (createdFromDate) {
      params = params.set('createdFromDate', createdFromDate);
    }
    if (createdToDate) {
      params = params.set('createdToDate', createdToDate);
    }
    if (addedBy) {
      params = params.set('addedBy', addedBy);
    }
    if (clinic) {
      params = params.set('clinicId', clinic);
    }
    if (category) {
      params = params.set('category', category);
    }

    return this.http.get<any>(this.apiUrl, {
      headers: this.commonService.getRequestHeaders(),
      params: params,
    });
  }

  getProcessScheduler(id: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${id}`, {
      headers: this.commonService.getRequestHeaders(),
    });
  }

  createProcess(data: any): Observable<any> {
    return this.http.post(this.apiUrl, data, {
      headers: this.commonService.getRequestHeaders(),
    });
  }

  updateProcess(id: string | null, data: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/${id}`, data, {
      headers: this.commonService.getRequestHeaders(),
    });
  }

  getProcessSchedulerTasks(
    clinicId?: string,
    skip?: number,
    limit?: number,
    searchValue: string = '',
    status?: any,
    assignedBy?: string,
    category?: string,
    issueFound?: boolean,
    fromDate?: string,
    toDate?: string,
    assignedTo?: string,
    assigneeType?: string,
    sortBy: string = 'startDateTime',
    sortOrder: string = 'asc',
    auditorStatus?: any,
    isSelectedForAudit: boolean = false,
    isReviewer: boolean = false
  ): Observable<any> {
    let params = new HttpParams()
      .set('sortBy', sortBy)
      .set('sortOrder', sortOrder);

    if (skip) {
      params = params.set('skip', skip.toString());
    }
    if (limit) {
      params = params.set('limit', limit.toString());
    }
    if (searchValue) {
      params = params.set('filter', searchValue);
    }
    if (assignedBy) {
      params = params.set('assignedBy', assignedBy);
    }
    if (category) {
      params = params.set('category', category);
    }
    if (status) {
      params = params.set('status', status);
    }

    if (auditorStatus) {
      params = params.set('auditorStatus', auditorStatus);
    }

    if (issueFound) {
      params = params.set('issueFound', issueFound);
    }
    if (fromDate) {
      params = params.set('fromDate', fromDate);
    }
    if (toDate) {
      params = params.set('toDate', toDate);
    }
    if (assignedTo) {
      params = params.set('assignedTo', assignedTo);
    }
    if (assigneeType) {
      params = params.set('type', assigneeType);
    }
    if (isSelectedForAudit) {
      params = params.set('isSelectedForAudit', isSelectedForAudit);
    }

    if (isReviewer) {
      params = params.set('type', 'reviewer');
    }

    const url = clinicId
      ? `${environment.baseAPIUrl}/clinics/${clinicId}/tasks`
      : `${environment.baseAPIUrl}/tasks`;

    return this.http.get<any>(url, {
      headers: this.commonService.getRequestHeaders(),
      params,
    });
  }

  getProcessById(processId: string | null): Observable<any> {
    const url = `${this.apiUrl}/${processId}`;
    return this.http.get<any>(url, {
      headers: this.commonService.getRequestHeaders(),
    });
  }

  getProcessCount(
    status?: any,
    issueFound?: boolean,
    fromDate?: string,
    toDate?: string
  ): Observable<any> {
    const url = `${environment.baseAPIUrl}/tasks/counts-by-status`;
    let params = new HttpParams();

    if (status) {
      params = params.set('status', status);
    }
    if (issueFound) {
      params = params.set('issueFound', issueFound);
    }
    if (fromDate) {
      params = params.set('fromDate', fromDate);
    }
    if (toDate) {
      params = params.set('toDate', toDate);
    }

    return this.http.get<any>(url, {
      headers: this.commonService.getRequestHeaders(),
      params,
    });
  }

  getTaskById(taskId: string | null): Observable<any> {
    const url = `${environment.baseAPIUrl}/tasks/${taskId}`;
    return this.http.get<any>(url, {
      headers: this.commonService.getRequestHeaders(),
    });
  }

  completeTask(id: string | undefined, data: any): Observable<any> {
    return this.http.put(`${environment.baseAPIUrl}/tasks/${id}`, data, {
      headers: this.commonService.getRequestHeaders(),
    });
  }

  addTaskAttachments(taskId: string, formData: FormData): Observable<any> {
    return this.http.post(
      `${environment.baseAPIUrl}/tasks/${taskId}/attachments`,
      formData,
      { headers: this.commonService.getRequestHeaders() }
    );
  }

  deleteTaskAttachment(taskId: string, attachmentId: string): Observable<any> {
    return this.http.delete(
      `${environment.baseAPIUrl}/tasks/${taskId}/attachments/${attachmentId}`,
      { headers: this.commonService.getRequestHeaders() }
    );
  }

  auditTask(taskId: string, payload: any): Observable<any> {
    return this.http.patch(
      `${environment.baseAPIUrl}/tasks/${taskId}/audit`,
      payload,
      {
        headers: this.commonService.getRequestHeaders(),
      }
    );
  }

  createGroup(payload: any): Observable<any> {
    return this.http.post(`${environment.baseAPIUrl}/groups`, payload, {
      headers: this.commonService.getRequestHeaders(),
    });
  }

  updateGroup(groupId: string, payload: any): Observable<any> {
    return this.http.put(
      `${environment.baseAPIUrl}/groups/${groupId}`,
      payload,
      {
        headers: this.commonService.getRequestHeaders(),
      }
    );
  }

  getAllGroups(
    skip: number,
    limit: number,
    searchValue: string = '',
    fromDate?: string,
    toDate?: string,
    addedBy?: string
  ): Observable<any> {
    let params = new HttpParams()
      .set('skip', skip.toString())
      .set('limit', limit.toString())
      .set('sortBy', 'createdAt')
      .set('sortOrder', 'desc');

    if (searchValue) {
      params = params.set('filter', searchValue);
    }
    if (fromDate) {
      params = params.set('createdFromDate', fromDate);
    }
    if (toDate) {
      params = params.set('createdToDate', toDate);
    }
    if (addedBy) {
      params = params.set('addedBy', addedBy);
    }

    return this.http.get<any>(`${environment.baseAPIUrl}/groups/all`, {
      headers: this.commonService.getRequestHeaders(),
      params: params,
    });
  }

  getAllGroupsName(): Observable<any> {
    let params = new HttpParams();

    return this.http.get<any>(`${environment.baseAPIUrl}/groups/list`, {
      headers: this.commonService.getRequestHeaders(),
      params: params,
    });
  }

  getGroupMessages(
    groupId: string,
    limit: number = 20,
    beforeCursor: string = '',
    afterCursor: string = ''
  ): Observable<any> {
    let params = new HttpParams().set('limit', limit.toString());

    if (beforeCursor) {
      params = params.set('beforeCursor', beforeCursor);
    }
    if (afterCursor) {
      params = params.set('afterCursor', afterCursor);
    }

    return this.http.get<any>(
      `${environment.baseAPIUrl}/groups/${groupId}/messages`,
      {
        headers: this.commonService.getRequestHeaders(),
        params: params,
      }
    );
  }

  sendGroupMessage(groupId: string, payload: any): Observable<any> {
    return this.http.post(
      `${environment.baseAPIUrl}/groups/${groupId}/messages`,
      payload,
      {
        headers: this.commonService.getRequestHeaders(),
      }
    );
  }

  getGroupDetails(groupId: string): Observable<any> {
    return this.http.get<any>(`${environment.baseAPIUrl}/groups/${groupId}`, {
      headers: this.commonService.getRequestHeaders(),
    });
  }

  fetchFileThumbnailUrls(): Observable<any> {
    let url = `${environment.baseAPIUrl}/whatsapp-messages/file-placeholder`;
    return this.http.get<FilePlaceholderResponse>(url, {
      headers: this.commonService.getRequestHeaders(),
    });
  }
}
